import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { Mt5Account, PaginatedAccountHistory } from '@/types/api';

export function useMt5Accounts(opts?: { refetchInterval?: number | false }) {
  return useQuery<Mt5Account[]>({
    queryKey: ['mt5-accounts'],
    queryFn: async () => {
      const res = await api.get('/accounts/mt5');
      return res.data;
    },
    refetchInterval: opts?.refetchInterval,
  });
}

export function useCreateMt5Account() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      login_id: string;
      server: string;
      password: string;
      name?: string;
      currency?: string;
      leverage?: number;
      region?: string;
    }) => {
      const res = await api.post('/accounts/mt5', body);
      return res.data as Mt5Account;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export function useDeleteMt5Account() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mt5AccountId: string) => {
      await api.delete(`/accounts/mt5/${mt5AccountId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export function useSyncMt5Balance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mt5AccountId: string) => {
      const res = await api.post(`/accounts/mt5/${mt5AccountId}/sync-balance`);
      return res.data as Mt5Account;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export function useUpdateMt5Account() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string }) => {
      const res = await api.patch(`/accounts/mt5/${id}`, body);
      return res.data as Mt5Account;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mt5-accounts'] });
    },
  });
}

export interface AccountHistoryParams {
  page?: number;
  limit?: number;
  type?: 'all' | 'trades' | 'deposits' | 'withdrawals';
}

export function useAccountHistory(mt5AccountId: string, params?: AccountHistoryParams) {
  return useQuery<PaginatedAccountHistory>({
    queryKey: ['mt5-accounts', mt5AccountId, 'history', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.type) searchParams.set('type', params.type);
      const qs = searchParams.toString();
      const res = await api.get(`/accounts/mt5/${mt5AccountId}/history${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!mt5AccountId,
  });
}
